"use client";

import { useState, useEffect } from 'react';
import {
  AiOutlinePlus,
  AiOutlineEdit,
  AiOutlineDelete,
  AiOutlinePrinter,
  AiOutlineSave,
  AiOutlineReload,
} from 'react-icons/ai';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../utils/CONSTANTS';
import Header from '../components/header';
import { isSuperAdmin } from '../utils/roles';

export default function OrderEntry() {
  const [isMaximized, setIsMaximized] = useState(false);
  const [isFormEnabled, setIsFormEnabled] = useState(false);
  const [isNewEntry, setIsNewEntry] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [wireno, setwireno] = useState([]);
  const [partyNames, setPartyNames] = useState([]);
  const [userRole, setUserRole] = useState(''); // <-- new

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const cardNo = params.get("cardNo");
    if (cardNo) {
      fetchDataByCardNo(cardNo);
    }
  }, [location.search]);

  const [formData, setFormData] = useState({
    cardNo: '',
    wireName: '',
    partySpec: '',
    customPartySpec: '',
    date: '',
    drgNo: '',
    customDrgNo: '',
    mcNo: '',
    mcNoOther: '',
    type: '',
    osd: '',
    osodId: '',
    osl: '',
    ostColls: '',
    osactiveColls: '',
    osgrinding: '',
    mcd: '',
    mcodId: '',
    mcl: '',
    mctColls: '',
    mcactiveColls: '',
    mcgrinding: '',
    quantity: '',
    estimateWt: '',
    rate: '',
    estimateQty: '',
    wtReqd: '',
  });

  const [formError, setFormError] = useState({
    wireName: '',
    partySpec: '',
    date: '',
    drgNo: '',
    mcNo: '',
    quantity: '',
    estimateWt: '',
    rate: '',
    estimateQty: '',
    wtReqd: '',
  });

  const validateField = (name, value) => {
    if (!value || value.length === 0) {
      return `${name} is required.`;
    }
    return '';
  };

  const calculateweight = () => {
    const estimateQty = Number(formData.estimateQty);
    const quantity = Number(formData.quantity);
    const estimateWt = Number(formData.estimateWt);

    if (!isNaN(estimateQty) && !isNaN(quantity) && !isNaN(estimateWt) && quantity !== 0) {
      const sum = estimateQty / quantity;
      const answer = estimateWt / sum;
      const wtKg = Number((answer / 1000).toFixed(3)); // Kg with 3 decimals
      setFormData(prev => ({ ...prev, wtReqd: wtKg }));
    }
  };

  const fetchWireNo = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/wire/names`, {
        withCredentials: true,
      });
      const result = response.data.names.map((item) => item.name);
      setwireno(result || []);
    } catch (error) {
      console.error('Error fetching wire names:', error);
    }
  };

  const [allCardNumbers, setAllCardNumbers] = useState([]);
  const [allDrgNumbers, setAllDrgNumbers] = useState([]);
  const [allMcNumbers, setAllMcNumbers] = useState([]);
  const [drgNumDetails, setDrgNumDetails] = useState([]);

  // --- Show All states ---
  const [showAllActive, setShowAllActive] = useState(false); // toggled in header
  const [allOrders, setAllOrders] = useState([]); // server page results
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [selectedCards, setSelectedCards] = useState(new Set()); // cardNo strings
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 15;
  const [totalCount, setTotalCount] = useState(0);
  const [isSavingSelected, setIsSavingSelected] = useState(false);

  useEffect(() => {
    fetchWireNo();
    fetchAllCardNumbers();
    fetchPartynames();
    fetchAllDrgNos();
    fetchAllMcNos();

    // read user role from localStorage and default partySpec if user role is 'user'
    const role = localStorage.getItem('userRole') || '';
    setUserRole(role);
    if (role === 'user') {
      setFormData(prev => ({ ...prev, partySpec: 'USER' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetch paged orders when ShowAll opens or page changes
  useEffect(() => {
    if (showAllActive && isSuperAdmin(userRole)) {
      fetchAllOrders(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllActive, currentPage, userRole]);

  useEffect(() => {
    calculateweight();
  }, [formData.estimateQty, formData.quantity, formData.estimateWt]);

  const fetchAllDrgNos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/order/getDrgNos`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();

      const drgArray = data.drgNos || [];
      setDrgNumDetails(drgArray); // full objects
      setAllDrgNumbers(drgArray.map(item => (typeof item === 'string' ? item : item.drgNo)));
    } catch (error) {
      console.error('Error fetching drg numbers:', error);
    }
  };

  const fetchAllMcNos = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/order/getMCNos`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      setAllMcNumbers(data.mcNos.map((item) => item.mcNo));
    } catch (error) {
      console.error('Error fetching card mc numbers:', error);
    }
  };
  const fetchAllCardNumbers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/order/getCardno`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      const list = data.cardNos || [];
      setAllCardNumbers(list);
      return list;
    } catch (error) {
      console.error("Error fetching card numbers:", error);
      return [];
    }
  };

  const fetchPartynames = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/order/getPartynames`, {
        withCredentials: true,
      });

      // response.data.cardNos might be an array of partySpec strings
      const uniqueParties = [...new Set(response.data.cardNos.map(item => item))];
      setPartyNames(uniqueParties);
    } catch (err) {
      console.error("Error fetching party names:", err);
    }
  };

  const formatdata = (userdate) => {
    const formattedDate = new Date(userdate).toISOString().split('T')[0];
    return formattedDate;
  };

  const fetchDataByCardNo = async (cardNo) => {
    if (cardNo) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/order/getOrder/${encodeURIComponent(cardNo)}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
          }
        );
        const data = await response.json();
        if (!data.cardData) {
          return;
        }
        var userdata = data.cardData;
        if (userdata.date) {
          userdata.date = formatdata(userdata.date);
        }

        // Only fetch drawings if party is not 'USER'
        if (userdata.partySpec && userdata.partySpec !== 'USER') {
          fetchDrawingsForParty(userdata.partySpec);
        }

        setFormData(userdata);
        setIsFormEnabled(true);
        setIsNewEntry(false);
      } catch (error) {
        console.error('Error fetching specific data:', error);
      }
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      cardNo: '',
      wireName: '',
      partySpec: userRole === 'user' ? 'USER' : '',
      customPartySpec: '',
      date: '',
      drgNo: '',
      customDrgNo: '',
      mcNo: '',
      mcNoOther: '',
      type: '',
      osd: '',
      osodId: '',
      osl: '',
      ostColls: '',
      osactiveColls: '',
      osgrinding: '',
      mcd: '',
      mcodId: '',
      mcl: '',
      mctColls: '',
      mcactiveColls: '',
      mcgrinding: '',
      quantity: '',
      estimateWt: '',
      rate: '',
      estimateQty: '',
      wtReqd: '',
    });

    setIsFormEnabled(false);
    setIsNewEntry(false);
    setFormError({
      wireName: '',
      partySpec: '',
      date: '',
      drgNo: '',
      mcNo: '',
      quantity: '',
      estimateWt: '',
      rate: '',
      estimateQty: '',
      wtReqd: '',
    });
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    // Basic update
    setFormData(prev => ({ ...prev, [name]: value }));

    // If party changes, fetch drawings
    if (name === 'partySpec' && value && value !== 'USER') {
      fetchDrawingsForParty(value);
    }

    // when wireName updates, set osd to wireName
    if (name === 'wireName' && value) {
      setFormData(prev => ({ ...prev, wireName: value, osd: value }));
    }

    // when drgNo selected, populate related fields and fetch drawing estimates
    if (name === 'drgNo') {
      if (value && value !== 'Other') {
        // find details from drgNumDetails (if available)
        const selectedDrgDetails = (drgNumDetails || []).find((item) => {
          if (!item) return false;
          if (typeof item === 'string') return item === value;
          return item.drgNo === value;
        });

        if (selectedDrgDetails) {
          setFormData(prev => ({
            ...prev,
            wireName: selectedDrgDetails.osd || prev.wireName,
            osd: selectedDrgDetails.osd || prev.osd,
            osodId: selectedDrgDetails.osodId || prev.osodId,
            osl: selectedDrgDetails.osl || prev.osl,
            ostColls: selectedDrgDetails.ostColls || prev.ostColls,
            osactiveColls: selectedDrgDetails.osactiveColls || prev.osactiveColls,
            osgrinding: selectedDrgDetails.osgrinding || prev.osgrinding,
            mcNo: selectedDrgDetails.mcNo || prev.mcNo,
            mcd: selectedDrgDetails.mcd || prev.mcd,
            mcodId: selectedDrgDetails.mcodId || prev.mcodId,
            mcl: selectedDrgDetails.mcl || prev.mcl,
            mctColls: selectedDrgDetails.mctColls || prev.mctColls,
            mcactiveColls: selectedDrgDetails.mcactiveColls || prev.mcactiveColls,
            mcgrinding: selectedDrgDetails.mcgrinding || prev.mcgrinding,
          }));
        }

        // Fetch drawing metadata (estimateWt & estimateQty) from new Drawing collection
        fetchDrgDetails(value);
      }
    }

    if (name === 'rate' && value === '') {
      setFormError(prev => ({ ...prev, rate: '' }));
    }
  };

  const fetchDrawingsForParty = async (partySpec) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/order/getDrawingsByParty/${encodeURIComponent(partySpec)}`);
      console.log(`Fetched drawings for ${partySpec}:`, response.data.drgNos);
      setAllDrgNumbers(response.data.drgNos || []);
    } catch (error) {
      console.error("Error fetching drawings:", error);
      setAllDrgNumbers([]);
    }
  };

  const fetchDrgDetails = async (drgNo) => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/api/order/getDrawing/${encodeURIComponent(drgNo)}`);
      const drawing = resp.data.drawing;
      if (drawing) {
        setFormData(prev => ({
          ...prev,
          estimateWt: drawing.estimateWt ?? prev.estimateWt,
          estimateQty: drawing.estimateQty ?? prev.estimateQty,
        }));
      }
    } catch (err) {
      // not found is okay; clear nothing
      console.debug('No drawing metadata found for', drgNo, err?.response?.status);
    }
  };

  const handleAdd = async () => {
    if (isSaving) return;
    if (isNewEntry) {
      const errors = {
        wireName: validateField('wireName', formData?.wireName),
        // validate partySpec only for non-user roles
        partySpec: userRole === 'user' ? '' : validateField('partySpec', formData?.partySpec),
        date: validateField('date', formData?.date),
        drgNo: validateField('drgNo', formData?.drgNo),
        mcNo: validateField('mcNo', formData?.mcNo),
        quantity: validateField('quantity', formData?.quantity),
        estimateWt: validateField('estimateWt', formData?.estimateWt),
        estimateQty: validateField('estimateQty', formData?.estimateQty),
        wtReqd: validateField('wtReqd', formData?.wtReqd),
      };

      setFormError(errors);
      if (Object.values(errors).some((item) => item)) return;

      try {
        setIsSaving(true);
        // ensure numeric fields are numeric before POST
        const payload = {
          ...formData,
          // Force partySpec to 'USER' for user role
          partySpec: userRole === 'user' ? 'USER' : formData.partySpec,
          estimateWt: formData.estimateWt !== '' ? Number(formData.estimateWt) : undefined,
          estimateQty: formData.estimateQty !== '' ? Number(formData.estimateQty) : undefined,
          quantity: formData.quantity !== '' ? Number(formData.quantity) : undefined,
          wtReqd: formData.wtReqd !== '' ? Number(formData.wtReqd) : undefined,
        };

        const response = await axios.post(
          `${API_BASE_URL}/api/order/createOrder`,
          payload
        );
        if (response.status === 200) {
          toast('Order added Successfully');
          await fetchAllCardNumbers();
          resetForm();
          setIsNewEntry(false);
          setIsFormEnabled(false);
        }
      } catch (err) {
        const status = err?.response?.status;
        if (status === 409) {
          // Duplicate card/voucher no: refresh list and set next card no
          toast.error(err?.response?.data?.message || 'Duplicate Card/Voucher No. Please try again.');
          const latest = await fetchAllCardNumbers();
          const numbers = (latest || [])
            .map((item) => parseInt(item.cardNo))
            .filter((num) => !isNaN(num));
          const maxvalue = numbers.length > 0 ? Math.max(...numbers) : 0;
          setFormData(prev => ({ ...prev, cardNo: String(maxvalue + 1) }));
        } else {
          console.error('Error posting data:', err);
          toast.error('Error saving order (see console)');
        }
      } finally {
        setIsSaving(false);
      }
    } else {
      setIsNewEntry(true);
      setIsFormEnabled(true);
      const numbers = allCardNumbers
        .map((item) => parseInt(item.cardNo))
        .filter((num) => !isNaN(num));
      const maxvalue = numbers.length > 0 ? Math.max(...numbers) : 0;

      setFormData({
        wireName: '',
        partySpec: userRole === 'user' ? 'USER' : '',
        customPartySpec: '',
        date: new Date().toISOString().split('T')[0],
        drgNo: '',
        customDrgNo: '',
        mcNo: '',
        mcNoOther: '',
        type: '',
        osd: '',
        osodId: '',
        osl: '',
        ostColls: '',
        osactiveColls: '',
        osgrinding: '',
        mcd: '',
        mcodId: '',
        mcl: '',
        mctColls: '',
        mcactiveColls: '',
        mcgrinding: '',
        quantity: '',
        estimateWt: '',
        rate: '',
        estimateQty: '',
        wtReqd: '',
        cardNo: String(maxvalue + 1), // keep cardNo as string to match backend schema
      });
    }
  };

  const handleUpdate = async () => {
    if (isFormEnabled && !isNewEntry) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/order/updateOrder/${encodeURIComponent(formData.cardNo)}`,
          {
            method: "PUT",
            headers: {
              "Content-type": "application/json",
            },
            body: JSON.stringify(formData),
          }
        );
        const data = await response.json();

        if (!response.ok) {
          toast.error("Some error occurred please try again ");
          return;
        }
        toast.success("Data updated successfully");
        await fetchAllCardNumbers();
        // refresh party names to include any new custom party
        await fetchPartynames();
        resetForm();
      } catch (error) {
        console.error("Error updating data:", error);
      }
    } else {
      toast("Please select a record to update or enable the form.");
    }
  };

  const handleDelete = async () => {
    if (isFormEnabled && !isNewEntry) {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/order/deleteOrder/${encodeURIComponent(formData.cardNo)}`,
          {
            method: "DELETE",
          }
        );

        if (response.ok) {
          toast.success(`Data for Card deleted successfully!`);
          await fetchAllCardNumbers();
          resetForm();
        } else {
          const err = await response.json();
          console.error('Delete failed', err);
        }
      } catch (error) {
        console.error("Error deleting data:", error);
      }
    }
  };

  const handleSave = () => {
    if (isFormEnabled) {
      if (isNewEntry) {
        handleAdd();
      } else {
        handleUpdate();
      }
    }
  };

  // ------------- Show All: server-side paginated fetch -------------
  const fetchAllOrders = async (page = 1) => {
    setIsLoadingAll(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/order/getAllOrders?page=${page}&pageSize=${PAGE_SIZE}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await resp.json();
      if (resp.ok) {
        // attach editParty and customParty placeholders
        const ordersWithEdit = (data.orders || []).map((o) => ({ ...o, editParty: o.partySpec || '', customParty: '' }));
        setAllOrders(ordersWithEdit);
        setTotalCount(data.totalCount || 0);
        setCurrentPage(data.page || page);
        setSelectedCards(new Set());
      } else {
        console.error('Failed to fetch orders', data);
        setAllOrders([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching all orders:', err);
      setAllOrders([]);
      setTotalCount(0);
    } finally {
      setIsLoadingAll(false);
    }
  };

  const toggleShowAll = async () => {
    const newVal = !showAllActive;
    setShowAllActive(newVal);
    if (newVal && isSuperAdmin(userRole)) {
      setCurrentPage(1);
      await fetchAllOrders(1);
    }
  };

  // Selection handling for rows
  const handleCheckboxChange = (cardNo, checked) => {
    setSelectedCards(prev => {
      const next = new Set(prev);
      if (checked) next.add(cardNo);
      else next.delete(cardNo);
      return next;
    });
  };

  const handleCardPartyChange = (cardNo, newParty) => {
    setAllOrders(prev => prev.map(o => o.cardNo === cardNo ? { ...o, editParty: newParty } : o));
  };

  // Keep editParty as 'Other' while typing; store typed text in customParty
  const handleCustomPartyChange = (cardNo, value) => {
    setAllOrders(prev =>
      prev.map(o =>
        o.cardNo === cardNo ? { ...o, customParty: value, editParty: 'Other' } : o
      )
    );
  };

  // Save selected rows using bulk endpoint
  const handleSaveSelected = async () => {
    if (!isSuperAdmin(userRole)) return;
    if (selectedCards.size === 0) {
      toast.info('No cards selected');
      return;
    }

    const toUpdate = allOrders
      .filter(o => selectedCards.has(o.cardNo))
      .map(o => {
        const partyToSave = (o.editParty === 'Other' ? (o.customParty || '') : o.editParty) || '';
        return { cardNo: o.cardNo, partySpec: partyToSave };
      });

    if (toUpdate.length === 0) {
      toast.info('No valid updates found for selected cards');
      return;
    }

    setIsSavingSelected(true);
    try {
      const resp = await fetch(`${API_BASE_URL}/api/order/bulkUpdateParties`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(toUpdate),
      });
      const data = await resp.json();
      if (!resp.ok) {
        console.error('Bulk update failed', data);
        toast.error(data?.message || 'Bulk update failed');
        return;
      }
      toast.success('Selected cards updated successfully');
      // refresh current page
      await fetchAllOrders(currentPage);
      await fetchAllCardNumbers();

      // ← NEW: refresh party list so newly-created "Other" parties appear
      await fetchPartynames();

    } catch (err) {
      console.error('Error bulk updating selected:', err);
      toast.error('Error saving selected cards (see console)');
    } finally {
      setIsSavingSelected(false);
      setSelectedCards(new Set());
    }
  };

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));
  const goPrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const goNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  // helper to check if a row's party select should be enabled (only when row is selected)
  const isRowSelectable = (cardNo) => selectedCards.has(cardNo);

  return (
    <>
      <Header />

      <div className="p-2 flex items-center justify-center">
        <div
          className={`bg-gray-200 shadow-xl ${isMaximized ? "w-full h-full" : "w-[960px]"
            }`}>
          <div className="flex items-center justify-between px-1 py-0.5 bg-gradient-to-r from-[#173131] to-[#0d9698] text-white">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3"></div>
              <span className="text-sm">{showAllActive ? 'Show All — Order Entries' : 'Order\\Card Entry'}</span>
            </div>
            <div className="flex">
              {/* Toggle between Order Entry and Show All inside the same form */}
              {isSuperAdmin(userRole) && (
                <button
                  onClick={toggleShowAll}
                  className="px-2 py-0.5 mr-2 bg-white text-black rounded text-sm"
                >
                  {showAllActive ? 'Back to Entry' : 'Show All'}
                </button>
              )}

              <button
                className="px-2 py-0.5 hover:bg-blue-500"
                onClick={() => setIsMaximized(!isMaximized)}>
                <span className="text-sm">□</span>
              </button>
            </div>
          </div>

          {/* ---------- WHEN showAllActive is true (admin) show the table (same area/size) ---------- */}
          {showAllActive && isSuperAdmin(userRole) ? (
            <div className="p-2 bg-[#E0FFFF]">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-gray-700">
                  {isLoadingAll ? 'Loading...' : (totalCount ?? 0) + ' records'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveSelected}
                    disabled={selectedCards.size === 0 || isSavingSelected}
                    className={`px-3 py-1 rounded text-sm ${selectedCards.size > 0 && !isSavingSelected ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`}
                  >
                    {isSavingSelected ? 'Saving...' : `Save Selected (${selectedCards.size})`}
                  </button>
                </div>
              </div>

              {isLoadingAll ? (
                <div className="p-4 text-sm text-gray-600">Loading orders...</div>
              ) : allOrders.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">No orders found.</div>
              ) : (
                <>
                  <div className="overflow-x-auto max-h-[60vh]">
                    <table className="min-w-full text-sm border-collapse">
                      <thead>
                        <tr className="bg-gray-100 sticky top-0">
                          <th className="p-2 border w-12 text-center">Select</th>
                          <th className="p-2 border">Card No</th>
                          <th className="p-2 border">Date</th>
                          <th className="p-2 border">Wire Name</th>
                          <th className="p-2 border">Drg.No</th>
                          <th className="p-2 border">M/c No</th>
                          <th className="p-2 border">Qty</th>
                          <th className="p-2 border">Est Wt (g)</th>
                          <th className="p-2 border">WT Req (Kg)</th>
                          <th className="p-2 border">Current Party</th>
                          <th className="p-2 border">Select Party</th>
                        </tr>
                      </thead>

                      <tbody>
                        {allOrders.map((row) => (
                          <tr key={row.cardNo} className="hover:bg-gray-50">
                            <td className="p-2 border text-center">
                              <input
                                type="checkbox"
                                checked={selectedCards.has(row.cardNo)}
                                onChange={(e) => handleCheckboxChange(row.cardNo, e.target.checked)}
                              />
                            </td>

                            <td className="p-2 border">{row.cardNo}</td>
                            <td className="p-2 border">{row.date ? new Date(row.date).toISOString().split('T')[0] : ''}</td>
                            <td className="p-2 border">{row.wireName}</td>
                            <td className="p-2 border">{row.drgNo}</td>
                            <td className="p-2 border">{row.mcNo}</td>
                            <td className="p-2 border">{row.quantity}</td>
                            <td className="p-2 border">{row.estimateWt}</td>
                            <td className="p-2 border">{row.wtReqd}</td>
                            <td className="p-2 border">{row.partySpec}</td>
                            <td className="p-2 border">
                              <select
                                value={row.editParty || ''}
                                onChange={(e) => handleCardPartyChange(row.cardNo, e.target.value)}
                                className="w-full border rounded px-2 py-1 text-sm"
                                disabled={!isRowSelectable(row.cardNo)}
                              >
                                <option value="">-- Select Party --</option>
                                {partyNames.map((p, idx) => <option key={idx} value={p}>{p}</option>)}
                                <option value="Other">Other</option>
                              </select>

                              {row.editParty === 'Other' && isRowSelectable(row.cardNo) && (
                                <input
                                  className="mt-2 w-full border rounded px-2 py-1 text-sm"
                                  placeholder="Enter other party"
                                  value={row.customParty || ''}
                                  onChange={(e) => handleCustomPartyChange(row.cardNo, e.target.value)}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <div className="text-sm text-gray-600">Page {currentPage} of {totalPages}</div>
                    <div className="flex gap-2">
                      <button onClick={goPrev} disabled={currentPage === 1} className="px-3 py-1 rounded bg-gray-200 text-sm">Prev</button>
                      <button onClick={goNext} disabled={currentPage === totalPages} className="px-3 py-1 rounded bg-gray-200 text-sm">Next</button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            /* ---------- ORIGINAL ORDER ENTRY UI (unchanged) ---------- */
            <div className="p-2 bg-[#E0FFFF]">
              <div className="space-y-2">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-gray-700 mb-0.5 text-sm">
                      Card No.
                    </label>
                    <select
                      name="cardNo"
                      value={formData.cardNo}
                      onChange={(e) => {
                        handleInputChange(e);
                        fetchDataByCardNo(e.target.value);
                      }}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={isNewEntry}>
                      <option value="">Select Card No.</option>
                      {[...allCardNumbers]
                        .sort((a, b) => parseInt(b.cardNo) - parseInt(a.cardNo))
                        .map((tool) => (
                          <option key={tool.cardNo} value={tool.cardNo}>
                            {tool.cardNo}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label
                      htmlFor="wireName"
                      className="block text-gray-700 mb-0.5 text-sm">
                      Wire Name
                    </label>
                    <select
                      id="wireName"
                      name="wireName"
                      required
                      value={formData.wireName}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-1 text-sm focus:outline-none focus:ring focus:border-blue-300"
                      disabled={!isFormEnabled}>
                      <option value="">Select Wire Name</option>
                      {wireno?.length > 0 ? (
                        [...wireno]
                          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                          .map((value, index) => (
                            <option key={index} value={value} className="text-black bg-white">
                              {value}
                            </option>
                          ))
                      ) : (
                        <option value="">No Wire Names Available</option>
                      )}
                    </select>
                    {formError?.wireName && (
                      <p className="text-sm text-red-400 capitalize">
                        {formError?.wireName}
                      </p>
                    )}
                  </div>
                </div>

                <div>

                  {userRole === 'user' ? (
                    // For 'user' role we don't show the selector; party will be saved as 'USER'
                    <div className="text-sm text-gray-600"><span className="font-medium"></span></div>
                  ) : (
                    <>

                      <label className="block text-gray-700 mb-0.5 text-sm">
                        Party Spec.
                      </label>
                      {formData.partySpec && formData.partySpec !== "Other" && formData.partySpec !== "" ? (
                        <select
                          name="partySpec"
                          value={formData.partySpec}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded p-0.5 text-sm"
                          disabled={!isFormEnabled}
                        >
                          <option value="">Select Party</option>
                          {[...partyNames]
                            .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                            .map((party, index) => (
                              <option key={index} value={party} className="text-black">
                                {party}
                              </option>
                            ))}
                          <option value="Other" className="text-black">Other</option>
                        </select>
                      ) : (
                        <>
                          <select
                            name="partySpec"
                            value={formData.partySpec}
                            onChange={handleInputChange}
                            className="w-full border border-gray-300 rounded p-0.5 text-sm"
                            disabled={!isFormEnabled}
                          >
                            <option value="">Select Party</option>
                            {partyNames.map((party, index) => (
                              <option key={index} value={party} className="text-black">
                                {party}
                              </option>
                            ))}
                            <option value="Other" className="text-black">Other</option>
                          </select>

                          {/* Show text input if "Other" is selected */}
                          {formData.partySpec === "Other" && (
                            <input
                              type="text"
                              name="customPartySpec"
                              placeholder="Enter other party spec"
                              value={formData.customPartySpec || ""}
                              onChange={(e) =>
                                handleInputChange({
                                  target: {
                                    name: "customPartySpec",
                                    value: e.target.value,
                                  },
                                })
                              }
                              className="w-full border border-gray-300 rounded p-0.5 text-sm mt-2"
                            />
                          )}
                        </>
                      )}
                    </>
                  )}

                  {formError?.partySpec && (
                    <p className="text-sm text-red-400 capitalize">
                      {formError?.partySpec}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="block text-gray-700 mb-0.5 text-sm">
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      required
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}
                    />
                    {formError?.date && (
                      <p className="text-sm text-red-400 capitalize">
                        {formError?.date}
                      </p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-gray-700 mb-0.5 text-sm">
                      Card No.
                    </label>
                    <input
                      type="text"
                      name="cardNo"
                      value={formData.cardNo}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 mb-0.5 text-sm">
                    Drg.No.
                  </label>
                  <select
                    name="drgNo"
                    value={formData.drgNo}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded p-0.5 text-sm"
                    disabled={!isFormEnabled}
                  >
                    <option value="">Select Drawing</option>
                    {[...allDrgNumbers]
                      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                      .map((value, index) => (
                        <option key={index} value={value} className="text-black">
                          {value}
                        </option>
                      ))}
                    <option value="Other">Other</option>
                  </select>

                  {formData.drgNo === "Other" && (
                    <input
                      type="text"
                      name="customDrgNo"
                      placeholder="Enter other Drg.No."
                      value={formData.customDrgNo || ""}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: "customDrgNo",
                            value: e.target.value,
                          },
                        })
                      }
                      className="w-full border border-gray-300 rounded p-0.5 text-sm mt-2"
                    />
                  )}

                  {formError?.drgNo && (
                    <p className="text-sm text-red-400 capitalize">
                      {formError?.drgNo}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 mb-0.5 text-sm">
                    M/c No.
                  </label>
                  <>
                    <select
                      name="mcNo"
                      value={formData.mcNo}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (e.target.value !== "Other") {
                          setFormData(prev => ({ ...prev, mcNo: e.target.value }));
                        }
                      }}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}>
                      <option value="">Select M/c No.</option>
                      {[...new Set(allMcNumbers)]
                        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
                        .map((value, index) => (
                          <option key={index} value={value} className="text-black">
                            {value}
                          </option>
                        ))}
                      <option value="Other">Other</option>
                    </select>

                    {formData.mcNo === "Other" && isFormEnabled && (
                      <input
                        type="text"
                        name="mcNoOther"
                        value={formData.mcNoOther || ""}
                        onChange={handleInputChange}
                        placeholder="Enter M/c No."
                        className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      />
                    )}
                  </>
                  {formError?.mcNo && (
                    <p className="text-sm text-red-400 capitalize">
                      {formError?.mcNo}
                    </p>
                  )}
                </div>

                <div className="border border-gray-300 rounded-sm">
                  <table className="w-full rounded">
                    <thead>
                      <tr className="bg-gray-100 border-b border-gray-300 rounded">
                        <th className="border-r border-gray-300 p-0.5 text-left text-sm">
                          Type
                        </th>
                        <th className="border-r border-gray-300 p-0.5 text-left text-sm">
                          d
                        </th>
                        <th className="border-r border-gray-300 p-0.5 text-left text-sm">
                          OD/ID
                        </th>
                        <th className="border-r border-gray-300 p-0.5 text-left text-sm">
                          L
                        </th>
                        <th className="border-r border-gray-300 p-0.5 text-left text-sm">
                          T.Colls
                        </th>
                        <th className="border-r border-gray-300 p-0.5 text-left text-sm">
                          Active_Colls
                        </th>
                        <th className="p-0.5 text-left text-sm">Grinding</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="bg-white text-black border-b border-black ">
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="type"
                            value={"O/s"}
                            className="w-full bg-transparent text-sm "
                            disabled={!isFormEnabled}
                            readOnly
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="osd"
                            value={formData.osd}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="osodId"
                            value={formData.osodId}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="osl"
                            value={formData.osl}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="ostColls"
                            value={formData.ostColls}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="osactiveColls"
                            value={formData.osactiveColls}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="p-0.5 text-sm">
                          <input
                            type="text"
                            name="osgrinding"
                            value={formData.osgrinding}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                      </tr>
                      <tr className="bg-white text-black">
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="type"
                            value={"M/c."}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                            readOnly
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="mcd"
                            value={formData.mcd}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="mcodId"
                            value={formData.mcodId}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="mcl"
                            value={formData.mcl}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="mctColls"
                            value={formData.mctColls}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="border-r border-gray-300 p-0.5 text-sm">
                          <input
                            type="text"
                            name="mcactiveColls"
                            value={formData.mcactiveColls}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                        <td className="p-0.5 text-sm">
                          <input
                            type="text"
                            name="mcgrinding"
                            value={formData.mcgrinding}
                            onChange={handleInputChange}
                            className="w-full bg-transparent text-sm"
                            disabled={!isFormEnabled}
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-gray-700 rounded mb-0.5 text-sm">
                      Quantity
                    </label>
                    <input
                      type="text"
                      name="quantity"
                      required
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}
                    />
                    {formError?.quantity && (
                      <p className="text-sm text-red-400 capitalize">
                        {formError?.quantity}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-0.5 text-sm">
                      Estimate Wt (gm.)
                    </label>
                    <input
                      type="text"
                      name="estimateWt"
                      required
                      value={formData.estimateWt}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}
                    />
                    {formError?.estimateWt && (
                      <p className="text-sm text-red-400 capitalize">
                        {formError?.estimateWt}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-0.5 text-sm">
                      Estimate Qty
                    </label>
                    <input
                      type="text"
                      name="estimateQty"
                      required
                      value={formData.estimateQty}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}
                    />
                    {formError?.estimateQty && (
                      <p className="text-sm text-red-400 capitalize">
                        {formError?.estimateQty}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-0.5 text-sm">
                      Rate
                    </label>
                    <input
                      type="text"
                      name="rate"
                      value={formData.rate}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}
                    />
                    {formError?.rate && (
                      <p className="text-sm text-red-400 capitalize">
                        {formError?.rate}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-0.5 text-sm">
                      WT. Reqd. (Kg)
                    </label>
                    <input
                      type="text"
                      name="wtReqd"
                      required
                      value={formData.wtReqd}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded p-0.5 text-sm"
                      disabled={!isFormEnabled}
                      readOnly
                    />
                    {formError?.wtReqd && (
                      <p className="text-sm text-red-400 capitalize">
                        {formError?.wtReqd}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-between p-1 rounded bg-teal-700 flex-wrap ">
            <div className="flex gap-1">
              <button
                onClick={handleAdd}
                disabled={(!formData.wireName || !formData.cardNo) && isNewEntry}
                className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border rounded border-gray-400 text-sm">
                <AiOutlinePlus className="w-3 h-3" />
                {isNewEntry ? "Clear" : "Add"}
              </button>
              <button
                onClick={() => {
                  handleUpdate();
                }}
                disabled={!isFormEnabled || isNewEntry}
                className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border rounded border-gray-400 text-sm"
              >
                <AiOutlineEdit className="w-3 h-3" />
                Update
              </button>
              <button
                onClick={() => {
                  handleDelete();
                }}
                className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border rounded border-gray-400 text-sm">
                <AiOutlineDelete className="w-3 h-3" />
                Delete
              </button>
            </div>
            <div className="flex gap-1">
              {!showAllActive && (
                <button
                  className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border rounded border-gray-400 text-sm"
                  onClick={() => {
                    navigate("/orderpdf", { state: formData });
                  }}>
                  <AiOutlinePrinter className="w-3 h-3" />
                  Print
                </button>
              )}

              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border rounded border-gray-400 text-sm"
                disabled={!isFormEnabled || isSaving}>
                <AiOutlineSave className="w-3 h-3" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={resetForm}
                className="flex items-center gap-1 px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border rounded border-gray-400 text-sm">
                <AiOutlineReload className="w-3 h-3" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
